import { OwcInput, OwcIconButton, OwcListItem, OwcDropdown, OwcList } from '@one/react';
import React from 'react';
import '../../styles.scss';

/**
 * Renders component able to display sublists.
 *
 * @copyright Roche 2025
 * @author Geoffroy Maillol
 */
class SublistSelector extends React.Component {
    /**
     * Constructor
     *
     * @param props The properties passed
     */
    constructor(props) {
        super(props);
        this.state = {
          newSublistValue: "",
          triggerLabel: "",
          triggerPlusText: "",
        };
        this.listRef = React.createRef();
        this.inputRef = React.createRef();
        this.handleInputKeyDownCapture = this.handleInputKeyDownCapture.bind(this);
    }

    componentDidMount() {
      this.refreshTriggerLabel();
      this.syncListSelection();
      this.attachInputKeyDownGuard();
    }

    componentDidUpdate(prevProps) {
      if (prevProps.value !== this.props.value ||
          prevProps.availableItems !== this.props.availableItems) {
        this.refreshTriggerLabel();
        this.syncListSelection();
      }
      this.attachInputKeyDownGuard();
    }

    componentWillUnmount() {
      if (this.nativeInput) {
        this.nativeInput.removeEventListener("keydown", this.handleInputKeyDownCapture, true);
        this.nativeInput = null;
      }
    }

    /**
     * Attaches a capture-phase keydown listener to the footer input's native
     * <input>.
     *
     * The enclosing owc-list/owc-dropdown keydown manager listens on its own
     * host element (which sits between the input and the React root) and treats
     * Space plus the arrow/home/end keys as list actions, calling preventDefault
     * on them. React's onKeyDown is delegated at the root, so it runs too late to
     * stop that. A native capture-phase listener on the real input intercepts the
     * key first, keeping typing (spaces) and caret movement working and stopping
     * the page from scrolling when Space is pressed.
     */
    attachInputKeyDownGuard() {
      const host = this.inputRef.current;
      const native = host && host.querySelector ? host.querySelector("input") : null;
      if (native === this.nativeInput) {
        return;
      }
      if (this.nativeInput) {
        this.nativeInput.removeEventListener("keydown", this.handleInputKeyDownCapture, true);
      }
      this.nativeInput = native;
      if (native) {
        native.addEventListener("keydown", this.handleInputKeyDownCapture, true);
      }
    }

    handleInputKeyDownCapture(ev) {
      const listNavigationKeys = [" ", "ArrowUp", "ArrowDown", "ArrowLeft",
        "ArrowRight", "Home", "End"];
      if (listNavigationKeys.includes(ev.key)) {
        ev.stopPropagation();
      }
    }

    syncListSelection() {
      const list = this.listRef.current;
      if (!list) {
        return;
      }
      const selected = this.props.value || [];
      const current = Array.isArray(list.selected) ? list.selected : [];
      const same = current.length === selected.length &&
          current.every((value, index) => value === selected[index]);
      if (!same) {
        list.selected = selected;
      }
    }

    /**
     * Rebuilds the collapsed-trigger label from the current selection.
     *
     * owc-dropdown renders its own fallback trigger and does not derive a label
     * from the list on its own, so the selected item text is pushed into
     * fallbackInputProps (first value + a "+N" overflow indicator).
     */
    refreshTriggerLabel() {
      const selected = this.props.value || [];
      const available = this.props.availableItems || [];
      if (selected.length === 0) {
        this.setState({ triggerLabel: "", triggerPlusText: "" });
        return;
      }
      // When every available sublist is selected, collapse the trigger to a
      // single "All" label instead of "firstValue +N" so it matches the state
      // of the "All" header item.
      if (available.length > 0 && selected.length === available.length) {
        this.setState({ triggerLabel: "All", triggerPlusText: "" });
        return;
      }
      this.setState({
        triggerLabel: selected[0],
        triggerPlusText: selected.length > 1 ? `+${selected.length - 1}` : "",
      });
    }

    handleSelectedChange(newSelection) {
      // owc-list emits the select-all header item as an entry without a value;
      // drop those so only real sublist values are propagated.
      const selection = (newSelection || []).filter((value) => value);
      this.props.onSelectedValueChanged(selection);
    }

    handleClear() {
      this.props.onSelectedValueChanged([]);
    }

    handleSelectAll() {
      const available = this.props.availableItems || [];
      const selected = this.props.value || [];
      const next = selected.length === available.length ? [] : [...available];
      this.props.onSelectedValueChanged(next);
    }

    /**
     * Derives the tri-state checkbox value for the "All" header item from the
     * current selection (all / none / some).
     */
    selectAllCheckboxState() {
      const available = this.props.availableItems || [];
      const selected = this.props.value || [];
      if (selected.length === 0) {
        return false;
      }
      if (selected.length === available.length) {
        return true;
      }
      return "indeterminate";
    }

    handleNewItemChange(value) {
      this.setState({
        newSublistValue: value,
      });
    }

    handleNewItemKeyDown(ev) {
      if (ev.key === "Enter") {
        ev.preventDefault();
        this.addSublist();
      }
    }

    addSublist() {
      const updatedList = this.props.availableItems;
      const newValue = this.state.newSublistValue;
      if (newValue && newValue.trim() !== "" && !updatedList.includes(newValue)) {
        updatedList.push(newValue);
        this.props.onAddItem(updatedList);
      }
      this.setState({
        newSublistValue: "",
      });
    }

    renderItem(value) {
      // showSelectorControl + multiple make each option render its checkbox;
      // owc-select-dropdown showed these by default, owc-list does not.
      return (
        <OwcListItem key={"item" + value} id={"item" + value} value={value}
            showSelectorControl multiple>
          {value}
        </OwcListItem>
      );
    }

    render() {
      const fallbackInputProps = {
        label: "Sublist",
        value: this.state.triggerLabel,
        plusText: this.state.triggerPlusText,
      };
      return (
        <div>
          <OwcDropdown fallbackInputProps={fallbackInputProps}
              onFallbackClearClick={() => this.handleClear()}>
            <OwcListItem slot="header" className="sublist-select-all"
                showSelectorControl multiple managedCheckboxState
                checkboxState={this.selectAllCheckboxState()}
                onClick={() => this.handleSelectAll()}>
              All
            </OwcListItem>
            <OwcList ref={this.listRef} multiple
                selected={this.props.value || []}
                onSelectedChange={(ev) => this.handleSelectedChange(ev.detail)}>
              {this.props.availableItems.map(sublist => this.renderItem(sublist))}
            </OwcList>
            <OwcInput
                ref={this.inputRef}
                slot="footer"
                placeholder="Enter a new sublist..."
                value={this.state.newSublistValue}
                onKeyDown={(ev) => this.handleNewItemKeyDown(ev)}
                onValueChange={(ev) => this.handleNewItemChange(ev.detail)}>
              <OwcIconButton slot="suffix" icon="circle_add" onClick={() => this.addSublist()}/>
            </OwcInput>
          </OwcDropdown>
        </div>

      )
    }
}

export default SublistSelector;
